/**
 * Is a published bundle still runnable against this SDK?
 *
 * A widget bundle has the SDK compiled into it, so once packaged there is nothing
 * in the JS that says which SDK produced it. The only surviving record is the
 * `sdkVersion` the packaging step stamps into widget.json, and the only place that
 * record is trustworthy is the *registry* — the manifest the platform stored at
 * upload and pinned at activation. Everything in this file is therefore designed
 * to be fed host-side registry data. Never feed it a version a running bundle
 * reported over the port: that is the one input the bundle controls, and a gate
 * that reads it is a gate the bundle can talk its way past.
 *
 * Dependency-free on purpose. It runs in three places — the provisioner (Node),
 * the Angular render host, and the SDK's own tests — and none of them should have
 * to agree on a semver library for a gate this small.
 */

import { MINIMUM_COMPATIBLE_SDK_VERSION, SDK_BREAKING_CHANGES, WidgetManifest } from './protocol';

/**
 * The SDK version a manifest with no `sdkVersion` is read as.
 *
 * A stored row cannot be re-stamped — a published version is immutable — so a
 * manifest that predates the field has to be read as *something*. It is read as
 * the first App SDK version, 0.1.0, which keeps it working under today's floor
 * and makes it the *first* thing stranded when the floor rises. That is the
 * intended behaviour: the default buys compatibility now, not forever.
 *
 * This is a read-time default only. New uploads must carry the field (the
 * provisioner's ajv schema requires it), so nothing new can arrive here by
 * omitting it and inheriting the most permissive answer.
 */
export const GRANDFATHERED_SDK_VERSION = '0.1.0';

export interface SdkCompatibilityResult {
  compatible: boolean;
  /** Present exactly when `compatible` is false; safe to show an operator. */
  reason?: string;
}

/** Release-ordering compare. Any prerelease sorts below its release. */
export function compareSdkVersions(a: string, b: string): number {
  const left = parseSdkVersion(a);
  const right = parseSdkVersion(b);
  if (!left || !right) {
    throw new Error(`Not a semver SDK version: ${!left ? a : b}`);
  }
  for (const part of ['major', 'minor', 'patch'] as const) {
    if (left[part] !== right[part]) {
      return left[part] - right[part];
    }
  }
  if (left.prerelease === right.prerelease) return 0;
  if (!left.prerelease) return 1;
  if (!right.prerelease) return -1;
  return left.prerelease < right.prerelease ? -1 : 1;
}

export function isSemverSdkVersion(value: unknown): value is string {
  return typeof value === 'string' && parseSdkVersion(value) !== null;
}

/**
 * The recorded breaks that affect a bundle built against `bundleSdkVersion` —
 * every entry introduced *after* it. Newest first, so the most recent reason
 * leads.
 */
export function sdkBreakingChangesSince(
  bundleSdkVersion: string,
  changes: { since: string; reason: string }[] = SDK_BREAKING_CHANGES,
): { since: string; reason: string }[] {
  if (!isSemverSdkVersion(bundleSdkVersion)) return [];
  return changes
    .filter(({ since }) => isSemverSdkVersion(since) && compareSdkVersions(since, bundleSdkVersion) > 0)
    .sort((a, b) => compareSdkVersions(b.since, a.since));
}

/**
 * Whether a bundle stamped `bundleSdkVersion` may run against this SDK.
 *
 * Two ways to fail, and only two:
 * - the stamp is not a semver string at all (a hand-edited or truncated
 *   manifest) — unverifiable, so refused rather than assumed fine;
 * - the stamp is below `floor`.
 *
 * A bundle built against a *newer* SDK than the host deliberately passes. The
 * protocol grows by addition, so the worst case is the bundle calling something
 * this host does not serve, which the bridge already answers with
 * 'unknown-operation'/'unknown-topic' — a defined outcome the widget can render
 * around. Refusing it would break the normal state of affairs during a rollout,
 * where bundles are packaged against a newer SDK than a not-yet-updated project
 * runs.
 */
export function isSdkVersionCompatible(
  bundleSdkVersion: string,
  floor: string = MINIMUM_COMPATIBLE_SDK_VERSION,
): SdkCompatibilityResult {
  if (!isSemverSdkVersion(bundleSdkVersion)) {
    return {
      compatible: false,
      reason: `sdkVersion ${JSON.stringify(
        bundleSdkVersion,
      )} is not a semver version, so the SDK the bundle was built against cannot be established — repackage it with current tooling`,
    };
  }
  if (compareSdkVersions(bundleSdkVersion, floor) >= 0) {
    return { compatible: true };
  }
  const ledger = sdkBreakingChangesSince(bundleSdkVersion)
    .map(({ since, reason }) => `${reason} (SDK ${since})`)
    .join('; ');
  return {
    compatible: false,
    reason:
      `built against SDK ${bundleSdkVersion}, minimum is ${floor}` +
      (ledger ? ` — ${ledger}` : ' — republish the bundle against the current SDK'),
  };
}

/**
 * The SDK version a *stored* manifest is to be read as: what it says, or the
 * grandfather version when it predates the field. Use this everywhere a manifest
 * comes out of the registry — the gates must all read the same number.
 */
export function manifestSdkVersion(manifest: Pick<WidgetManifest, 'sdkVersion'> | null | undefined): string {
  return manifest?.sdkVersion ?? GRANDFATHERED_SDK_VERSION;
}

function parseSdkVersion(
  value: string,
): { major: number; minor: number; patch: number; prerelease: string } | null {
  const match =
    /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-([0-9a-zA-Z-.]+))?(?:\+[0-9a-zA-Z-.]+)?$/.exec(value);
  if (!match) return null;
  return {
    major: Number(match[1]),
    minor: Number(match[2]),
    patch: Number(match[3]),
    prerelease: match[4] ?? '',
  };
}
